<template>
    <div
        class="bg-black relative col-span-3 grid grid-cols-subgrid gap-0 transition-[scale,margin] duration-300 *:transition-opacity *:duration-300"
        :class="{ '[&>*:not([data-overlay]):not([data-killed-avatar])]:opacity-40': killed }"
        :style="[glowStyle, sizeStyle]"
        :data-size="size"
        data-testid="killer-player-row"
    >
        <Transition
            enter-active-class="transition-opacity duration-300"
            leave-active-class="transition-opacity duration-300"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
        >
            <div
                v-if="overlay"
                data-overlay
                class="@container pointer-events-none absolute inset-0 z-20 flex"
                :class="
                    killed
                        ? 'clip-right-bottom-corner col-[2/-1] bg-[#1F1F1F] px-2.5 py-1.5'
                        : 'bg-[#222]/90'
                "
                data-testid="killer-bar-overlay"
            >
                <div
                    class="flex flex-1 items-center justify-center"
                    :class="{ 'clip-diagonal-corners bg-[#282828]': killed }"
                >
                    <span class="font-title text-center text-md font-bold text-white uppercase">{{ overlay }}</span>
                </div>
            </div>
        </Transition>
        <div class="absolute top-0 left-0 -translate-y-full text-xs uppercase font-bold flex playercard-clip" :style="{backgroundColor: color}">
            <div class="w-7 relative">
               <div class="w-7 h-7 absolute left-0 bottom-0 clip-left-bottom-corner-small" :style="{ backgroundColor: getLighterColor(color, 40) }">
                <div class="inset-px absolute clip-left-bottom-corner-small"  :style="{ backgroundColor: color }">
                    <img
                        v-if="player.avatar"
                        v-avatar-fallback="player.avatar"
                        :alt="$t('general.avatar_alt')"
                        class="w-full h-full object-cover absolute left-0 bottom-0"
                        />
                </div>
               </div> 
            </div>
            <span class="px-5">
                {{ player.tag_name }}
            </span>
        </div>
        <div :class="killed ? 'p-0.5' : 'p-2'" :data-killed-avatar="killed || null">
            <div
                v-if="killed"
                class="flex aspect-square w-11 rounded-sm border p-0.5"
                :style="{ borderColor: killerPalette.shadow }"
                data-testid="killer-killed-avatar"
            >
                <div class="flex flex-1 items-center justify-center rounded-xs border" :style="killedGlowStyle">
                    <KillerCrossIcon :color="color" class="w-[72%]" />
                </div>
            </div>
            <div
                v-else
                class="clip-left-bottom-corner relative flex h-8 w-8 items-center justify-center overflow-hidden"
                :style="{ backgroundColor: getLighterColor(color, 40) }"
            >
                <div
                    class="clip-left-bottom-corner font-title absolute inset-px flex items-center justify-center text-xs font-bold"
                    :style="{ backgroundColor: color }"
                >
                    {{ targetSection }}
                    <svg v-if="isKiller" class="absolute w-full h-full" viewBox="0 0 30 30">
                        <g filter="url(#filter0_d_23038_20081)">
                        <path d="M20.4325 24.3278H15.7334V20.4158H13.4034V24.3278H8.70434V20.4158H4.38712C3.62342 20.4158 3 19.7846 3 19.0131V5.51598C3 4.74449 3.62342 4.11328 4.38712 4.11328H24.7419C25.5056 4.11328 26.129 4.74449 26.129 5.51598V19.0131C26.129 19.7846 25.5056 20.4158 24.7419 20.4158H20.4247V24.3278H20.4325ZM15.9828 24.0863H20.1909V20.1742H24.7497C25.3809 20.1742 25.8953 19.6521 25.8953 19.0209V5.52378C25.8953 4.88477 25.3809 4.37041 24.7497 4.37041H4.39492C3.7637 4.37041 3.24938 4.89256 3.24938 5.52378V19.0209C3.24938 19.6599 3.7637 20.1742 4.39492 20.1742H8.95369V24.0863H13.1618V20.1742H15.9828V24.0863Z" :fill="getLighterColor(color, 80)"/>
                        <path d="M3 4.27798V17.7751C3 18.4843 3.56886 19.0532 4.27801 19.0532H8.76667V22.9652H13.2709V19.0532H15.8737V22.9652H20.378V19.0532H24.8666C25.5758 19.0532 26.1446 18.4843 26.1446 17.7751V4.27798C26.1446 3.56884 25.5758 3 24.8666 3H4.28581C3.57666 3 3.0078 3.56884 3.0078 4.27798H3ZM11.837 12.024H5.51709V9.31216H11.837V12.024ZM12.8969 14.5177L14.5723 12.5072L16.2478 14.5177H12.8969ZM23.6276 12.024H17.3076V9.31216H23.6276V12.024Z" :fill="getLighterColor(color, 80)"/>
                        </g>
                        <defs>
                        <filter id="filter0_d_23038_20081" x="0" y="0" width="30" height="30" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dx="7" dy="7"/>
                        <feGaussianBlur stdDeviation="5"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.75 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_23038_20081"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_23038_20081" result="shape"/>
                        </filter>
                        </defs>
                    </svg>
                </div>
            </div>
        </div>

        <!-- Status points + lives bars -->
        <div class="clip-diagonal-corners relative flex h-full w-full bg-zinc-700">
            <div
                class="bg-black clip-diagonal-corners absolute left-0.5 flex h-full w-[calc(100%-4px)] p-2"
            >
                <div class="relative flex w-full flex-col gap-1">
                    <div
                        class="flex h-[30%] items-center gap-1.5"
                        role="meter"
                        :aria-label="$t('killer.status_points')"
                        :aria-valuenow="statusPoints"
                        aria-valuemin="0"
                        :aria-valuemax="maxStatusPoints"
                        data-testid="killer-status-points"
                        :data-value="statusPoints"
                    >
                        <svg class="h-full max-h-2.5 w-auto shrink-0" viewBox="0 0 7 8" aria-hidden="true">
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M3.48794 0C4.79316 0.846909 5.97228 1.24758 6.98425 1.15286C7.16099 4.81209 5.84087 6.9731 3.50146 7.875C1.24232 7.03085 -0.0939524 4.96283 0.00515337 1.09736C1.19311 1.161 2.35859 0.898114 3.48794 0ZM3.51611 1.25245C4.40619 1.82994 5.2103 2.10314 5.90035 2.03867C6.02086 4.53396 5.12065 6.00757 3.52538 6.62255C3.5153 6.61877 3.50541 6.61486 3.49539 6.61101V1.26866L3.51611 1.25245ZM3.48932 0.452133C4.64465 1.20176 5.68843 1.55648 6.5842 1.47259C6.74059 4.71161 5.5721 6.62454 3.5014 7.4228C1.50163 6.67561 0.318686 4.84516 0.40646 1.42343C1.45806 1.47983 2.48968 1.24713 3.48932 0.452133Z"
                                :fill="color"
                            />
                        </svg>
                        <div class="clip-diagonal-corners-small flex h-full flex-1 gap-0.5">
                            <div
                                v-for="barIndex in maxStatusPoints"
                                :key="barIndex"
                                class="flex-1 bg-zinc-800 transition-all"
                                :style="barIndex <= statusPoints ? getGradientStyle(barIndex) : {}"
                            ></div>
                        </div>
                    </div>
                    <div
                        class="clip-diagonal-corners flex flex-1 gap-0.5"
                        role="meter"
                        :aria-label="$t('killer.lives')"
                        :aria-valuenow="lives"
                        aria-valuemin="0"
                        :aria-valuemax="maxLives"
                        data-testid="killer-lives"
                        :data-value="lives"
                    >
                        <div
                            v-for="lifeIndex in maxLives"
                            :key="lifeIndex"
                            class="flex flex-1 items-center justify-center transition-all"
                            :class="{ 'bg-zinc-800': lifeIndex > lives }"
                            :style="lifeIndex <= lives ? { backgroundColor: color } : {}"
                        >
                            <svg
                                class="h-3.5 w-auto transition-opacity"
                                :class="{ 'opacity-25': lifeIndex > lives }"
                                viewBox="0 0 13 12"
                                aria-hidden="true"
                            >
                                <path
                                    d="M6.33333 3.44644L4.81667 2.10161L3.40833 1.89471L2 3.03265V4.58437L6.33333 9.65333V3.44644Z"
                                    :fill="`url(#${heartGradientId}-${lifeIndex}-left)`"
                                />
                                <path
                                    d="M6.33325 3.44644L7.84992 2.10161L9.25825 1.89471L10.6666 3.03265V4.58437L6.33325 9.65333V3.44644Z"
                                    :fill="`url(#${heartGradientId}-${lifeIndex}-right)`"
                                />
                                <path
                                    d="M6.33333 3.44644L4.81667 2.10161L3.40833 1.89471L2 3.03265V4.58437L6.33333 9.65333L10.6667 4.58437V3.03265L9.25833 1.89471L7.85 2.10161L6.33333 3.44644Z"
                                    fill="none"
                                    :stroke="heartColors.stroke"
                                />
                                <defs>
                                    <linearGradient
                                        :id="`${heartGradientId}-${lifeIndex}-left`"
                                        x1="2"
                                        y1="1.89471"
                                        x2="2"
                                        y2="9.65333"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop :stop-color="heartColors.leftTop" />
                                        <stop offset="1" :stop-color="heartColors.leftBottom" />
                                    </linearGradient>
                                    <linearGradient
                                        :id="`${heartGradientId}-${lifeIndex}-right`"
                                        x1="6.33325"
                                        y1="1.89471"
                                        x2="6.33325"
                                        y2="9.65333"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop :stop-color="heartColors.rightTop" />
                                        <stop offset="1" :stop-color="heartColors.rightBottom" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Plus/Minus buttons (for own score bar) -->
        <div class="flex h-12 gap-1 pl-1" v-if="mode === 'score'">
            <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm font-bold text-black transition-colors"
                :class="buttonClasses"
                data-testid="killer-plus"
                @click="canInteract ? $emit('click-plus') : null"
            >
                <img
                    src="@/assets/images/games/killer/plus.svg"
                    :alt="$t('killer.plus_icon_alt')"
                />
            </div>
            <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm font-bold text-black transition-colors"
                :class="minusButtonClasses"
                data-testid="killer-minus"
                @click="canRemove ? $emit('click-minus') : null"
            >
                 <img
                    src="@/assets/images/games/killer/minus.svg"
                    :alt="$t('killer.minus_icon_alt')"
                />
            </div>
        </div>

        <template v-if="mode === 'kill'">
            <div
                class="flex h-12 w-full items-center justify-center rounded-sm text-sm font-bold text-black uppercase transition-colors"
                :class="buttonClasses"
                data-testid="killer-kill"
                @click="canInteract ? $emit('click-kill') : null"
            >
                 <img
                    src="@/assets/images/games/killer/kill.svg"
                    :alt="$t('killer.kill_icon_alt')"
                    class="h-full"
                />
            </div>
        </template>
    </div>
</template>

<script>
import { getDarkerColor, getLighterColor } from '@/utils/colorHelpers'
import { getKillerPalette } from './killerPalette.js'
import KillerCrossIcon from './ui/KillerCrossIcon.vue'

let instanceCount = 0

const SIZE_SCALES = {
    large: 1,
    normal: 0.9,
    small: 0.75,
}

export default {
    name: 'PlayerScoreBar',
    components: { KillerCrossIcon },
    data() {
        return {
            heartGradientId: `killer-heart-${++instanceCount}`,
            rowHeight: 0,
            resizeObserver: null,
        }
    },
    props: {
        player: {
            type: Object,
            required: true,
        },
        color: {
            type: String,
            default: '#FFFFFF',
        },
        statusPoints: {
            type: Number,
            default: 0,
        },
        maxStatusPoints: {
            type: Number,
            default: 3,
        },
        lives: {
            type: Number,
            default: 0,
        },
        maxLives: {
            type: Number,
            default: 3,
        },
        /** 'score' = plus/minus buttons, 'kill' = single kill button, 'none' = no buttons */
        mode: {
            type: String,
            default: 'none',
            validator: (v) => ['score', 'kill', 'none'].includes(v),
        },
        canInteract: {
            type: Boolean,
            default: false,
        },
        canRemove: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'normal',
            validator: (v) => Object.keys(SIZE_SCALES).includes(v),
        },
        overlay: {
            type: String,
            default: null,
        },
        killed: {
            type: Boolean,
            default: false,
        },
        isActive: {
            type: Boolean,
            default: false,
        },
        targetSection: {
            type: [String, Number],
            default: null,
        },
        isKiller: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['click-plus', 'click-minus', 'click-kill'],
    computed: {
        buttonClasses() {
            if (this.killed) {
                return 'cursor-not-allowed opacity-50'
            }
            if (this.canInteract) {
                return 'cursor-pointer'
            }
            return 'cursor-not-allowed opacity-50'
        },
        minusButtonClasses() {
            if (this.canRemove) {
                return 'cursor-pointer'
            }
            return 'cursor-not-allowed opacity-50'
        },
        heartColors() {
            return {
                leftTop: this.getLighterColor(this.color, 68),
                leftBottom: this.getDarkerColor(this.color, 11),
                rightTop: this.getLighterColor(this.color, 36),
                rightBottom: this.getDarkerColor(this.color, 30),
                stroke: this.getLighterColor(this.color, 63),
            }
        },
        killerPalette() {
            return getKillerPalette(this.color)
        },
        killedGlowStyle() {
            const stops = this.killerPalette.glow
                .map((stop) => `${stop.color} ${stop.offset * 100}%`)
                .join(', ')
            return {
                borderColor: this.killerPalette.band,
                background: `radial-gradient(closest-side, ${stops})`,
            }
        },
        glowStyle() {
            if (!this.isActive) return {}
            return {
                boxShadow: `0 0 20px 4px ${this.color}`,
            }
        },
        sizeStyle() {
            const scale = SIZE_SCALES[this.size]
            const offset = ((scale - 1) * this.rowHeight) / 2
            const spacing = this.isActive ? 1.25 : 1
            return {
                scale,
                marginTop: `calc(${spacing * scale}rem + ${offset}px)`,
                marginBottom: `calc(${this.isActive ? spacing : 0}rem + ${offset}px)`,
            }
        },
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(([entry]) => {
            this.rowHeight = entry.target.offsetHeight
        })
        this.resizeObserver.observe(this.$el)
    },
    beforeUnmount() {
        this.resizeObserver?.disconnect()
    },
    methods: {
        getLighterColor,
        getDarkerColor,
        getGradientStyle(index) {
            const endColor = this.getLighterColor(this.color, 40 - index * 5)
            const startColor = this.getDarkerColor(this.color, 10 + index * 5)
            return {
                background: `linear-gradient(90deg, ${startColor} 0%, ${endColor} 100%)`,
            }
        },
    },
}
</script>


<style lang="scss">
    .playercard-clip{
        clip-path: polygon(
            0 -100px, 
            0 calc(100% - 7px), 
            7px 100%, 
            100% 100%, 
            calc(100% - 15px) 0,
            100% 0);
        padding-right: 25px;
    }
</style>